// @flow
import lz from 'lz-string';
import URI from 'urijs';
import CustomError from 'error.js';
import { get, identity, pickBy } from 'lodash';
import axios from 'axios';
import moment from 'moment';

import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { httpHeaders, windowWrapper } from '@clubmed/utils';

import config from 'Config';
import logger from 'Logger';
import { ACCESS_TOKEN } from 'Utils/ple-utils/cookies';
import { getQueryResult, setQueryResult } from './cacheFront';

// This is the key matching the content containing the translations in the CMS
export const COMMON_CMS_CONTENT = 'common';
export const B2C_USER_AGENT = 'cm-b2c';
export const TIMESTAMP_QUERY_PARAM = 't';
export const GraphQLRequestFailed = CustomError.create({
  name: 'GraphQLRequestFailed',
  message: 'The GraphQL request failed',
});
export const PAGES_CMS_CONTENT = 'pages';

export type QueryOptions = {
  requestName: string,
  queryId?: string,
  prebuiltArgs?: {},
  cacheFront?: { key: string, expirationDate?: * },
  strictErrors?: boolean,
};

function cleanGQLQuery(query: string) {
  return query.replace(/\s\s+/g, ' ');
}

function encodeGQLQuery(query: string) {
  return config.get('graphql.compressedRequests') ? lz.compressToEncodedURIComponent(query) : query;
}

function onResponse({ data: { data } }) {
  return data;
}

function onRequestError(query) {
  return error => {
    const gqlError = new GraphQLRequestFailed({
      message: error.message,
      errors: get(error, 'response.data.errors') || [],
      query: query,
    });
    logger.error(gqlError);
    return Promise.reject(gqlError);
  };
}

function getAxiosInstance({ gqlQuery }) {
  const axiosInstance = axios.create();
  axiosInstance.interceptors.response.use(onResponse, onRequestError(gqlQuery));
  return axiosInstance;
}

function getHeaders({
  requestId,
  origin,
  requestName,
  isDirectCxo,
  isIndirectCxo,
  isCmta,
  cacheable,
  bypassCache,
  authenticationToken,
  customerNeolaneId,
  strictErrors,
}) {
  return pickBy(
    {
      isDirectCxo,
      isIndirectCxo,
      isCmta,
      cookie:
        typeof window === 'undefined' && authenticationToken
          ? `${ACCESS_TOKEN}=${authenticationToken}`
          : null,
      'user-agent': typeof window === 'undefined' ? B2C_USER_AGENT : undefined,
      [httpHeaders.X_REFERER]: origin,
      [httpHeaders.X_REQUEST_NAME]: requestName,
      [httpHeaders.X_PARENT_ID]: requestId,
      [httpHeaders.X_GRAPH_ID]: requestId,
      [httpHeaders.CM_STRICT_ERRORS]: strictErrors,
      ...(bypassCache === true ? { [httpHeaders.X_BYPASS_CACHE]: true } : null),
      ...(!cacheable ? { 'cache-control': httpHeaders.NO_CACHE } : null),
      customerNeolaneId,
    },
    identity,
  );
}

function encodePrebuiltArgs(args: {}) {
  const stringArgs = JSON.stringify(args);

  return config.get('graphql.compressedRequests')
    ? lz.compressToEncodedURIComponent(stringArgs)
    : stringArgs;
}

function getGraphqlEndpoint({
  locale,
  pagesContent,
  pagesRelease,
  cmsContent,
  cmsRelease,
  cacheable,
  gqlQuery,
  queryId,
  prebuiltArgs,
}) {
  return new URI(config.get('graphql.host'))
    .query({
      query: cacheable && !queryId ? encodeGQLQuery(cleanGQLQuery(gqlQuery)) : undefined,
      queryId: queryId ? queryId : undefined,
      prebuiltArgs: prebuiltArgs ? encodePrebuiltArgs(prebuiltArgs) : undefined,
      locale,
      cmsConfig: JSON.stringify([
        { type: COMMON_CMS_CONTENT, release: cmsRelease, content: cmsContent },
        { type: PAGES_CMS_CONTENT, release: pagesRelease, content: pagesContent },
      ]),
      [TIMESTAMP_QUERY_PARAM]: !cacheable ? moment().format('x') : undefined, // To prevent the query to be cached on browsers like Safari, that remove no cache headers
      version: config.get('APP_VERSION'), // to prevent cache between each B2C version
    })
    .toString();
}

function runQuery(
  gqlQuery: string,
  appContext: $Shape<AppContext> = {},
  {
    requestName = '',
    queryId = '',
    prebuiltArgs = {},
    cacheFront: { key: cacheKey, expirationDate: cacheExpirationDate } = {},
    strictErrors = true,
  }: QueryOptions = {
    requestName: '',
    queryId: '',
    prebuiltArgs: {},
    cacheFront: {},
    strictErrors: true,
  },
  cacheable: boolean = true,
): Promise<*> {
  const {
    locale,
    cmsRelease,
    cmsContent,
    pagesContent,
    pagesRelease,
    authenticationToken,
    isDirectCxo,
    isIndirectCxo,
    originalUrl,
    requestId,
    isCmta,
    customerNeolaneId,
    bypassCache = false,
  } = appContext;

  const cacheFrontEnabled = isFeatureFlipped(appContext)('cacheFrontEnabled');
  const frontCachedResult = getQueryResult(cacheKey, customerNeolaneId);
  if (!bypassCache && cacheFrontEnabled && frontCachedResult) {
    return Promise.resolve(frontCachedResult);
  }

  const postRequest = !cacheable && !queryId;

  const requestOptions = {
    url: getGraphqlEndpoint({
      gqlQuery,
      locale,
      cmsRelease,
      cmsContent,
      pagesContent,
      pagesRelease,
      cacheable,
      queryId,
      prebuiltArgs,
    }),
    method: postRequest ? 'post' : 'get',
    data: postRequest ? { query: encodeGQLQuery(cleanGQLQuery(gqlQuery)) } : undefined,
    withCredentials: true,
    headers: getHeaders({
      requestId,
      origin: typeof window === 'undefined' ? originalUrl : windowWrapper.getLocation(),
      requestName,
      isDirectCxo,
      isIndirectCxo,
      isCmta,
      cacheable,
      bypassCache,
      authenticationToken,
      customerNeolaneId,
      strictErrors,
    }),
  };

  return getAxiosInstance({ gqlQuery })
    .request(requestOptions)
    .then(result => {
      if (cacheFrontEnabled && cacheKey) {
        setQueryResult({ cacheKey, cacheExpirationDate, result, customerNeolaneId });
      }
      return result;
    });
}

function runNoCacheQuery(
  gqlQuery: string,
  appContext: $Shape<AppContext>,
  options?: QueryOptions,
): Promise<*> {
  return runQuery(gqlQuery, appContext, options, false);
}

export default {
  runQuery,
  runNoCacheQuery,
};
